"use client"

import { cn, Modal, ModalBody, ModalContent, Spinner } from "@heroui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useRef, useState } from "react"
import { deleteAgentSkill, type AgentSkill } from "@/apis/agent"
import { useSkills } from "./skillCatalog"
import { normalizeSkillIcon, useSkillText } from "./SkillsMenu"

const CloseIcon = ({ className }: { className?: string }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className={className}>
    <path d="M13 5 5 13M5 5l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const PlayGlyph = ({ className }: { className?: string }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" className={className}>
    <path d="M5.4 3.9c0-1 .8-1.6 1.8-1.1l7 4.1c1 .6 1 1.9 0 2.5l-7 4.1c-1 .5-1.8-.1-1.8-1.1V3.9Z" />
  </svg>
)

const TrashIcon = ({ className }: { className?: string }) => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" className={className}>
    <path
      d="M2 3.5h10M5.5 3.5V2.3a.8.8 0 0 1 .8-.8h1.4a.8.8 0 0 1 .8.8v1.2m2.3 0v8a1 1 0 0 1-1 1H4.2a1 1 0 0 1-1-1v-8M5.7 6.3v4M8.3 6.3v4"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/** 技能卡片（对齐小云雀：淡彩圆角封面 + 中文主标题 + 英文副题 + 悬停视频预览） */
function SkillStoreCard({
  skill,
  onUse,
  onDelete,
}: {
  skill: AgentSkill
  onUse: (skill: AgentSkill) => void
  onDelete?: (skill: AgentSkill) => void
}) {
  const t = useTranslations("generatePage")
  const text = useSkillText()(skill)
  const videoRef = useRef<HTMLVideoElement>(null)
  const hasVideo = !!skill.video

  return (
    <button
      type="button"
      onClick={() => onUse(skill)}
      onMouseEnter={() => videoRef.current?.play().catch(() => {})}
      onMouseLeave={() => videoRef.current?.pause()}
      className="group/skill flex w-full flex-col items-center text-center"
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-create-card">
        {skill.cover ? (
          <img
            src={skill.cover}
            alt={text.name}
            loading="lazy"
            className={cn(
              "absolute inset-0 size-full object-cover transition-opacity duration-200",
              hasVideo && "group-hover/skill:opacity-0",
            )}
          />
        ) : (
          <span className="absolute inset-0 grid place-content-center text-5xl">{normalizeSkillIcon(skill.icon)}</span>
        )}
        {hasVideo && (
          <video
            ref={videoRef}
            src={skill.video}
            muted
            loop
            playsInline
            preload="none"
            className="absolute inset-0 size-full object-cover opacity-0 transition-opacity duration-200 group-hover/skill:opacity-100"
          />
        )}
        {/* 视频角标 */}
        {hasVideo && (
          <span className="absolute right-2 top-2 grid size-7 place-content-center rounded-lg bg-theme-black/45 text-white backdrop-blur-sm">
            <PlayGlyph className="size-3.5" />
          </span>
        )}
        {/* 我的技能删除 */}
        {skill.is_mine && onDelete && (
          <span
            role="button"
            tabIndex={0}
            aria-label={t("delete" as any)}
            onClick={(e) => {
              e.stopPropagation()
              onDelete(skill)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation()
                onDelete(skill)
              }
            }}
            className="absolute left-2 top-2 hidden size-7 place-content-center rounded-lg bg-theme-black/45 text-white backdrop-blur-sm group-hover/skill:grid"
          >
            <TrashIcon className="size-3.5" />
          </span>
        )}
      </div>
      <p className="mt-2.5 line-clamp-1 text-[15px] font-semibold leading-5 text-theme-primary">{text.name}</p>
      <p className="mt-0.5 line-clamp-1 text-xs leading-4 text-theme-primary/45">{text.desc}</p>
    </button>
  )
}

/**
 * 技能商店：官方 / 我的分栏，卡片预览（封面/视频/介绍），
 * 「创建技能」进入 create-skill 对话流程（Agent 沉淀技能，对齐小云雀 Skill 商店）。
 */
export function SkillStoreModal({
  isOpen,
  onOpenChange,
  onSelect,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (skill: AgentSkill) => void
}) {
  const t = useTranslations("generatePage")
  const { official, mine, isLoading } = useSkills()
  const [tab, setTab] = useState<"official" | "mine">("official")
  const queryClient = useQueryClient()

  const deleteMutation = useMutation({
    mutationFn: deleteAgentSkill,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent", "skills"] }),
  })

  const list = tab === "official" ? official : mine

  const use = (skill: AgentSkill) => {
    onOpenChange(false)
    onSelect(skill)
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideCloseButton
      size="3xl"
      scrollBehavior="inside"
      classNames={{ base: "rounded-2xl bg-surface", body: "p-0" }}
    >
      <ModalContent>
        <ModalBody>
          {/* 头部：居中大标题 + 右上关闭 */}
          <div className="relative px-6 pt-6 text-center">
            <h2 className="text-xl font-bold text-theme-primary">{t("skillStore")}</h2>
            <p className="mt-1 text-xs text-theme-primary/40">{t("skillStoreDesc")}</p>
            <button
              type="button"
              aria-label="Close"
              onClick={() => onOpenChange(false)}
              className="absolute right-4 top-4 grid size-8 place-content-center rounded-md text-theme-primary/50 transition-colors hover:bg-theme-primary/5 hover:text-theme-primary"
            >
              <CloseIcon className="size-4.5" />
            </button>
          </div>

          {/* 分栏（文本下划线 tab）+ 创建技能 */}
          <div className="mt-4 flex items-center gap-4 border-b border-theme-primary/[0.06] px-6">
            <div className="flex items-center gap-4">
              {(["official", "mine"] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={cn(
                    "relative pb-2 text-sm transition-colors",
                    tab === key
                      ? "font-semibold text-theme-primary after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full after:bg-theme-primary"
                      : "text-theme-primary/45 hover:text-theme-primary/70",
                  )}
                >
                  {key === "official" ? t("skillOfficial") : t("skillMine")}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                const createSkill =
                  official.find((s) => s.id === "create-skill") ??
                  ({ id: "create-skill", icon: "🧪", name: "Create Skill", description: "", target: "agent" } as AgentSkill)
                use(createSkill)
              }}
              className="ml-auto pb-2 text-sm font-medium text-purple-blue transition-colors hover:opacity-80"
            >
              {t("createSkill")}
            </button>
          </div>

          <div className="min-h-[300px] px-6 pb-6 pt-5">
            {isLoading ? (
              <div className="grid h-60 place-content-center">
                <Spinner size="lg" color="current" />
              </div>
            ) : list.length === 0 ? (
              <div className="grid h-60 place-content-center gap-2 text-center">
                <p className="text-sm text-theme-primary/40">{t("skillMineEmpty")}</p>
                <button
                  type="button"
                  onClick={() => {
                    const createSkill =
                      official.find((s) => s.id === "create-skill") ??
                      ({ id: "create-skill", icon: "🧪", name: "Create Skill", description: "", target: "agent" } as AgentSkill)
                    use(createSkill)
                  }}
                  className="text-sm font-medium text-purple-blue hover:opacity-80"
                >
                  {t("createSkill")}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-x-4 gap-y-6 sm:grid-cols-4">
                {list.map((skill) => (
                  <SkillStoreCard
                    key={skill.id}
                    skill={skill}
                    onUse={use}
                    onDelete={
                      tab === "mine"
                        ? (s) => !deleteMutation.isPending && deleteMutation.mutate({ skill_id: s.id })
                        : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
